import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Loading from '../../molecules/Loading'
import useBlueprintPresentation from './hooks/useBlueprintPresentation'
import useBlueprintScopeResolution, { deriveScopePredicate } from './hooks/useBlueprintScopeResolution'
import BlueprintPresentationTable from './BlueprintPresentationTable'
import BlueprintDataGrid from './BlueprintDataGrid'
import BlueprintMetric from './BlueprintMetric'
import BlueprintChart from './BlueprintChart'
import BlueprintHierarchicalTable from './BlueprintHierarchicalTable'
import BlueprintHierarchicalGrid from './BlueprintHierarchicalGrid'
import BlueprintScopeSelector from './BlueprintScopeSelector'
import BlueprintHierarchicalPivotTable from './BlueprintHierarchicalPivotTable'

const PRESENTATION_TYPES = {
  'Presentation Table': BlueprintPresentationTable,
  'Data Grid': BlueprintDataGrid,
  Metric: BlueprintMetric,
  Chart: BlueprintChart,
  'Hierarchical Table': BlueprintHierarchicalTable,
  'Hierarchical Grid': BlueprintHierarchicalGrid,
  'Scope Selector': BlueprintScopeSelector,
  'Hierarchical Pivot Table': BlueprintHierarchicalPivotTable
}

/**
 * Self-describing blueprint presentation resolver.
 *
 * Fetches the blueprint definition by code, reads the presentation config
 * for the given key, and delegates to the appropriate renderer based on
 * the presentation type. React Query deduplicates the blueprint fetch
 * when multiple instances share the same blueprintCode.
 */
function QBPPresentation ({ blueprintCode, presentationKey, parameters, rendererProps }) {
  const {
    presentationConfig,
    definition,
    isLoading,
    error
  } = useBlueprintPresentation(blueprintCode, presentationKey)

  const purposeCode = presentationConfig?.config?.purposeCode ??
    presentationConfig?.config?.hierarchy?.levels?.[0]?.purposeCode
  const purposeData = definition?.data?.purposes?.[purposeCode]

  const scopeResolution = useBlueprintScopeResolution(
    purposeData?.scopePredicates,
    presentationConfig?.scope?.enabled
  )

  const scopePredicates = useMemo(() => {
    const pred = deriveScopePredicate(scopeResolution)
    return pred ? [pred] : []
  }, [scopeResolution])

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-danger-500)' }}>
        Failed to load blueprint &quot;{blueprintCode}&quot;: {error.message ?? 'Unknown error'}
      </div>
    )
  }

  if (!presentationConfig) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        No presentation &quot;{presentationKey}&quot; found for blueprint &quot;{blueprintCode}&quot;
      </div>
    )
  }

  const Renderer = PRESENTATION_TYPES[presentationConfig.type]

  if (!Renderer) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        Unknown presentation type &quot;{presentationConfig.type}&quot; for blueprint &quot;{blueprintCode}&quot;
      </div>
    )
  }

  return (
    <Renderer
      {...rendererProps}
      blueprintCode={blueprintCode}
      config={presentationConfig.config}
      displayName={presentationConfig.displayName}
      description={presentationConfig.description}
      subordinatePredicates={purposeData?.subordinatePredicates ?? []}
      latch={purposeData?.latch ?? null}
      purposeColumns={purposeData?.columns ?? []}
      parameters={parameters}
      scopePredicates={scopePredicates}
    />
  )
}

QBPPresentation.propTypes = {
  /** The blueprint code to load */
  blueprintCode: PropTypes.string.isRequired,

  /** The key within presentation.options to render */
  presentationKey: PropTypes.string.isRequired,

  /** Optional runtime parameters for variable substitution */
  parameters: PropTypes.object,

  /** Optional props forwarded directly to the underlying renderer */
  rendererProps: PropTypes.object
}

QBPPresentation.defaultProps = {
  parameters: {},
  rendererProps: {}
}

export default QBPPresentation
